import moment from 'moment';
import { path } from 'ramda';

import { bookingStatus as bookingStatusTypes } from 'types/booking';
import filterLegsByProductItemRefs from 'utils/filterLegsByProductItemRefs';
import { isMoreThanXMinutesAgo } from 'utils/dates';
import { isVoucherPayment } from 'utils/payment';
import { COVID_VOUCHER_REFUND } from 'constants/payment';
import { isBookingMultiJourney } from 'utils/booking';
import { DIRECTIONS } from 'types/classes';
import { EUROSTAR_ORGANIZATION } from 'types/constants';
import { SubscriptionStatusCode } from 'constants/subscriptions';
import disallowedAddPaxProductFamilies from 'constants/addPaxProductFamilies';
import { types } from 'types/passengers';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { NEW_FARE_CONDITION_DATE_ON_EXCHANGE } from 'constants/booking';
import { brandCode, DB_BRAND_CODES } from 'constants/brandCodes';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { isDBLeg } from 'utils/segmentTypes';

const SN_BRAND_CODE = [brandCode.Lyria, brandCode.SNCF, brandCode.INOUI];
const SNCB_BRAND_CODE = [brandCode.SNCB];
const NS_BRAND_CODE = [brandCode.NS];

export const isMembershipAccountHolderTravelling = ({ selectedItemRefs, booking, customerInfo }) => {
  const { allBookingItems } = booking;

  return selectedItemRefs
    .flatMap((ref) => allBookingItems.filter(({ itemRef }) => itemRef === ref).map(({ passenger }) => passenger))
    .some(
      ({ firstName, lastName }) =>
        firstName?.toLowerCase() === customerInfo?.firstName?.toLowerCase() &&
        lastName?.toLowerCase() === customerInfo?.lastName?.toLowerCase()
    );
};

const isBeforeToday = (date) => {
  const todayStart = moment().startOf('day');
  const tripDeparture = moment(date);

  return tripDeparture.isValid() && tripDeparture.isBefore(todayStart, 'day');
};

const isDepartureBeforeToday = (selectedItemRefs, booking) => {
  if (selectedItemRefs.length) {
    const directions = selectedItemRefs.map((itemRef) => findDirectionOfProductByItemRef(booking, itemRef));
    const uniqueDirection = [...new Set(directions)];
    const isoDates = uniqueDirection.flatMap((direction) =>
      booking[direction]
        ?.filter((item) => item.products.some((p) => selectedItemRefs.includes(p.itemRef)))
        .map((item) => item.timing?.isoDate)
    );
    return isoDates.every((isoDate) => isBeforeToday(isoDate));
  }
  return false;
};

const getPassengersForItemRefs = ({ allBookingItems, selectedItemRefs }) =>
  allBookingItems.filter((item) => selectedItemRefs.includes(item.itemRef))?.map((item) => item.passenger);
const getUniquePassengersbyId = ({ passengers }) =>
  passengers.reduce((acc, current) => {
    const paxsIds = acc.find((item) => item.id === current.id);
    return !paxsIds ? [...acc, current] : acc;
  }, []);

const disableAfterSalesForSubscription = ({ wasPurchasedWithSubscription, agent, subscription }) => {
  if (agent?.organization !== EUROSTAR_ORGANIZATION) return false;
  return (
    wasPurchasedWithSubscription &&
    subscription?.status !== SubscriptionStatusCode.ACTIVE &&
    subscription?.status !== SubscriptionStatusCode.NOT_STARTED
  );
};

const findDirectionOfProductByItemRef = (booking, itemRef) =>
  booking?.outbound?.find((o) => o.products?.find((p) => p.itemRef === itemRef))
    ? DIRECTIONS.OUTBOUND
    : booking?.inbound?.find((o) => o.products?.find((p) => p.itemRef === itemRef))
    ? DIRECTIONS.INBOUND
    : '';

const selectedItemsArePartOfASummatedFare = (booking, selectedItemRefs, isOutboundSummatedFare, isInboundSummatedFare) => {
  let summatedFareOutbound = false;
  let summatedFareInbound = false;

  if (isOutboundSummatedFare) {
    const outbound = booking?.outbound?.map((i) => i.products?.filter((p) => selectedItemRefs?.includes(p.itemRef))?.flat())?.flat();

    summatedFareOutbound = outbound?.length > 0;
  }

  if (isInboundSummatedFare) {
    const inbound = booking?.inbound?.map((i) => i.products?.filter((p) => selectedItemRefs?.includes(p.itemRef))?.flat())?.flat();

    summatedFareInbound = inbound?.length > 0;
  }

  return summatedFareOutbound || summatedFareInbound;
};

const checkIsEurostarRedAndBlue = (journeys) =>
  journeys.some(({ serviceType: { code } }) => code === serviceTypeCodes.EUROSTAR) &&
  journeys.some(({ serviceType: { code } }) => code === serviceTypeCodes.GREENSPEED);

const noItemsSelected = ({ selectedItemRefs = [] }) => selectedItemRefs.length === 0;
const disableCtaPerStatus = ({ status, isLyriaBooking = false, isSNCFBooking = false, isDBBooking = false, isNSBooking = false }) =>
  status === bookingStatusTypes.PENDING_REFUND ||
  ![
    bookingStatusTypes.CONFIRMED,
    bookingStatusTypes.TEMPORARILY_ON_HOLD,
    bookingStatusTypes.ON_HOLD,
    ...(isLyriaBooking || isSNCFBooking || isDBBooking || isNSBooking ? [bookingStatusTypes.PROVISIONAL] : [])
  ].includes(status);
const noItemsSelectedOrStatusNotAllowed = ({
  selectedItemRefs,
  status,
  isLyriaBooking = false,
  isSNCFBooking = false,
  isDBBooking = false,
  isNSBooking = false
}) => noItemsSelected({ selectedItemRefs }) || disableCtaPerStatus({ status, isLyriaBooking, isSNCFBooking, isDBBooking, isNSBooking });

const getAssociatedItemRefsForSelectedItems = ({ booking: { allBookingItems }, selectedItemRefs }) => {
  const associatedItemRefs = allBookingItems?.reduce((acc, item) => {
    const itemSelected = selectedItemRefs?.some((selectedItemRef) => item.itemRef === selectedItemRef);

    return itemSelected ? acc.concat([item.itemRef, ...item.associatedItemRefs]) : acc;
  }, []);

  return [...new Set(associatedItemRefs)];
};

const getTicketNumberForCheckedInTickets = ({ booking, selectedItemRefs }) => {
  const eurostarItems =
    booking &&
    booking.eurostarBookingItems &&
    booking.eurostarBookingItems.filter(
      (eurostarBookingItem) => selectedItemRefs.includes(eurostarBookingItem.itemRef) && eurostarBookingItem.isCheckedIn
    );

  const greenspeedItems =
    booking &&
    booking.greenspeedBookingItems &&
    booking.greenspeedBookingItems.filter(
      (greenspeedBookingItems) => selectedItemRefs.includes(greenspeedBookingItems.itemRef) && greenspeedBookingItems.isCheckedIn
    );

  const items = [...(eurostarItems || []), ...(greenspeedItems || [])];

  return Array.isArray(items) && !!items.length && items.map((checkedInItem) => checkedInItem.ticketNumber);
};

const getTicketNumbers = ({ booking, selectedItemRefs }) => {
  const eurostarItems =
    booking &&
    booking.eurostarBookingItems &&
    booking.eurostarBookingItems.filter((eurostarBookingItem) => selectedItemRefs.includes(eurostarBookingItem.itemRef));

  const greenspeedItems =
    booking &&
    booking.greenspeedBookingItems &&
    booking.greenspeedBookingItems.filter((greenspeedBookingItems) => selectedItemRefs.includes(greenspeedBookingItems.itemRef));

  const items = [...(eurostarItems || []), ...(greenspeedItems || [])];

  return Array.isArray(items) && !!items.length && items.map((item) => item.ticketNumber);
};

const getSegmentsForItemRefs = ({ inbound, outbound }, itemRefs) => {
  const inboundSegments = filterLegsByProductItemRefs(inbound, itemRefs);
  const outboundSegments = filterLegsByProductItemRefs(outbound, itemRefs);

  return new Set([...outboundSegments, ...inboundSegments]);
};

const runAfterSaleValidator =
  (booking, selectedItemRefs, user, func = 'every') =>
  (validators) => {
    const segments = Array.from(getSegmentsForItemRefs(booking, selectedItemRefs));

    return validators[func]((validator) => validator({ segments, selectedItemRefs, booking, user }));
  };

const bookingIsConfirmed = ({ booking }) => booking.displayStatus === bookingStatusTypes.CONFIRMED;

const hasActiveInsurance = ({ booking }) =>
  !!(
    booking.insurances &&
    booking.insurances.length &&
    booking.insurances.some((insurance) => !insurance.cancelled && !insurance.provisional)
  );

const selectedProductsThroughFare = (selectedItemRefs, { outbound, inbound }) => {
  const selectedLegs = [...getSegmentsForItemRefs({ outbound, inbound }, selectedItemRefs)];
  const throughFare = selectedLegs.some(({ isThroughFare }) => isThroughFare);
  return throughFare;
};

const selectedProductsCheckedIn =
  (func = 'every') =>
  ({ selectedItemRefs, booking: { eurostarBookingItems = [], greenspeedBookingItems = [] } }) =>
    selectedItemRefs.length > 0 && (eurostarBookingItems.length > 0 || greenspeedBookingItems.length > 0)
      ? [...eurostarBookingItems, ...greenspeedBookingItems].filter((p) => selectedItemRefs.includes(p.itemRef))[func]((p) => p.isCheckedIn)
      : false;

const selectedProductsCheckedInOrPrrAttempted = ({
  selectedItemRefs,
  booking: { eurostarBookingItems = [], greenspeedBookingItems = [] }
}) => {
  if (selectedItemRefs.length > 0 && (eurostarBookingItems.length > 0 || greenspeedBookingItems.length > 0)) {
    const bookingItems = [...eurostarBookingItems, ...greenspeedBookingItems].filter((p) => selectedItemRefs.includes(p.itemRef));

    return bookingItems.every((p) => p.isCheckedIn) || bookingItems.every((p) => p.isPrrAttempted);
  }

  return false;
};

const selectedProductsFamilyAllowedForAddPax = ({
  selectedItemRefs,
  booking: { eurostarBookingItems = [], greenspeedBookingItems = [] }
}) => {
  if (selectedItemRefs.length > 0 && (eurostarBookingItems.length > 0 || greenspeedBookingItems.length > 0)) {
    return [...eurostarBookingItems, ...greenspeedBookingItems]
      .filter((p) => selectedItemRefs.includes(p.itemRef))
      .every(({ productFamily = {} }) => !disallowedAddPaxProductFamilies.has(productFamily?.familyGroup));
  }

  return false;
};

const selectedProductsCheckedInOrConfirmedOrPrrAttempted = ({
  selectedItemRefs,
  booking: { eurostarBookingItems = [], greenspeedBookingItems = [] }
}) => {
  if (selectedItemRefs.length > 0 && (eurostarBookingItems.length > 0 || greenspeedBookingItems.length > 0)) {
    const esBookingItems = eurostarBookingItems.filter((p) => selectedItemRefs.includes(p.itemRef));
    const erBookingItems = greenspeedBookingItems.filter((p) => selectedItemRefs.includes(p.itemRef));

    return (
      (esBookingItems.every((p) => p.isCheckedIn) &&
        erBookingItems.every(
          (p) => p.isCheckedIn || (!p.isCheckedIn && !p.isPrrRefunded && p.isActive && !p.isCancelled && !p.isPrrAttempted)
        )) ||
      (esBookingItems.every((p) => p.isPrrAttempted) && erBookingItems.every((p) => p.isPrrAttempted))
    );
  }

  return false;
};

const selectedProductsHaveDifferentProductFamily = ({
  selectedItemRefs,
  booking: { eurostarBookingItems = [], greenspeedBookingItems = [] }
}) => {
  let uniqueProductFamilies = new Set();

  const eurostarProductFamilies =
    selectedItemRefs.length > 0 && eurostarBookingItems.length > 0
      ? eurostarBookingItems.filter((p) => selectedItemRefs.includes(p.itemRef)).map((p) => p.productFamily.familyGroup)
      : [];

  const greenspeedProductFamilies =
    selectedItemRefs.length > 0 && greenspeedBookingItems.length > 0
      ? greenspeedBookingItems.filter((p) => selectedItemRefs.includes(p.itemRef)).map((p) => p.productFamily.familyGroup)
      : [];
  uniqueProductFamilies = new Set([...uniqueProductFamilies, ...eurostarProductFamilies, ...greenspeedProductFamilies]);

  return uniqueProductFamilies.size > 1;
};

const allSelectedProductsConfirmedCheckedInOrPRRRefunded = ({
  selectedItemRefs,
  booking: { eurostarBookingItems = [], greenspeedBookingItems = [] }
}) =>
  selectedItemRefs.length > 0 && [...eurostarBookingItems, ...greenspeedBookingItems].length > 0
    ? [...eurostarBookingItems, ...greenspeedBookingItems]
        .filter((p) => selectedItemRefs.includes(p.itemRef))
        .every((p) => p.isActive && !p.isCancelled)
    : false;

const allSelectedProductsConfirmed = ({ selectedItemRefs, booking: { eurostarBookingItems = [], greenspeedBookingItems = [] } }) =>
  selectedItemRefs.length > 0 && eurostarBookingItems.length > 0
    ? eurostarBookingItems
        .filter((p) => selectedItemRefs.includes(p.itemRef))
        .every((p) => !p.isCheckedIn && !p.isPrrRefunded && p.isActive && !p.isCancelled)
    : selectedItemRefs.length > 0 && greenspeedBookingItems.length > 0
    ? greenspeedBookingItems.length > 0 &&
      greenspeedBookingItems
        .filter((p) => selectedItemRefs.includes(p.itemRef))
        .every((p) => !p.isCheckedIn && !p.isPrrRefunded && p.isActive && !p.isCancelled)
    : false;

const productHasNotBeenPrrRefunded = ({ selectedItemRefs, booking: { eurostarBookingItems } }) =>
  selectedItemRefs.length > 0 && eurostarBookingItems.length > 0
    ? eurostarBookingItems.filter((p) => selectedItemRefs.includes(p.itemRef)).every((p) => !p.isPrrRefunded)
    : true;

const allProductsHasNotBeenPrrRefunded = ({ selectedItemRefs, booking: { allBookingItems } }) =>
  selectedItemRefs.length > 0 && allBookingItems.length > 0
    ? allBookingItems.filter((p) => selectedItemRefs.includes(p.itemRef)).every((p) => !p.isPrrRefunded)
    : true;

const oneSegmentSelected = ({ segments }) => segments.length === 1;

const allSegmentsCanBeRebooked = ({ segments, selectedItemRefs = [] }) => {
  const hasActiveAOP = segments.some(({ activeBookingItems }) =>
    activeBookingItems
      .filter(({ itemRef }) => selectedItemRefs.includes(itemRef))
      .some(
        ({ canBeRebooked, afterSalesCurrentOverrides }) =>
          afterSalesCurrentOverrides &&
          afterSalesCurrentOverrides.length > 0 &&
          afterSalesCurrentOverrides.some(({ type }) => type === 'REBOOK') &&
          canBeRebooked
      )
  );

  if (hasActiveAOP) {
    return true;
  }

  return segments.every(({ activeBookingItems }) => {
    if (activeBookingItems && activeBookingItems.length > 0) {
      return activeBookingItems.every(({ canBeRebooked }) => canBeRebooked);
    }

    return false;
  });
};

const allSegmentsCannotBeRebooked = ({ segments = [], selectedItemRefs = [] }) => !allSegmentsCanBeRebooked({ segments, selectedItemRefs });

const allSegmentsCanBeCancelled = ({ segments }) =>
  segments.every(({ activeBookingItems }) => {
    if (activeBookingItems && activeBookingItems.length > 0) {
      return activeBookingItems.every(({ canBeCancelled }) => canBeCancelled);
    }

    return false;
  });

const allSegmentsDepartToday = ({ segments }) =>
  segments.every((segment) => moment(segment.timing.isoDate).isSame(moment(), 'day') && moment(segment.timing.isoDate).isAfter(moment()));

const allSegmentsAreMinutesBeforeDeparture =
  (minutes) =>
  ({ segments }) =>
    segments.every((segment) => isMoreThanXMinutesAgo(minutes)(segment.timing.isoDate));

const allSegmentsInOneJourney = ({ segments, booking: { additionalDetails = [] } = { additionalDetails: [] } }) => {
  const isMultiJourneyJourney = isBookingMultiJourney(additionalDetails);

  if (isMultiJourneyJourney) {
    const journeys = new Set();

    segments.forEach(({ id }) => {
      const journey = additionalDetails.find(({ key }) => key === id)?.value;

      if (journey) {
        journeys.add(journey);
      }
    });

    return journeys.size > 1 ? false : true;
  }

  return true;
};

const canceledPassengersInSegments = ({ booking, segments, selectedItemRefs }) => {
  const allBookingItems = booking?.allBookingItems || [];

  const isReturnAndNonCanceledJourneys =
    booking?.outbound?.length > 0 &&
    booking?.inbound?.length > 0 &&
    booking?.outbound.some(({ cancelled }) => !cancelled) &&
    booking?.inbound.some(({ cancelled }) => !cancelled);

  const isAllPassengerSelectedInSegment = segments.some(({ products }) =>
    products.every(({ itemRef }) => selectedItemRefs.includes(itemRef))
  );

  const selectedPassengers = new Set(
    allBookingItems.reduce((acc, { passengerId, itemRef }) => {
      if (selectedItemRefs.includes(itemRef)) {
        return [...acc, passengerId];
      }
      return acc;
    }, [])
  );

  if (isReturnAndNonCanceledJourneys && isAllPassengerSelectedInSegment && selectedPassengers.size > 1) {
    return allBookingItems.some(({ passengerId, cancelled }) => selectedPassengers.has(passengerId) && cancelled === true) ?? true;
  }

  return false;
};

const passengersWithMixedJourneysInSegments = ({ booking, segments, selectedItemRefs }) => {
  if (segments.length === 1) {
    const isReturnJourney = booking?.outbound?.length > 0 && booking?.inbound?.length > 0;

    const segmentDirection = segments[0]?.direction;

    const selectedPassengers = [
      ...new Set(
        segments[0].products.reduce((acc, { passengerId, itemRef }) => {
          if (selectedItemRefs.includes(itemRef)) {
            return [...acc, passengerId];
          }
          return acc;
        }, [])
      )
    ];

    const oppositeDirection = segmentDirection === DIRECTIONS.OUTBOUND ? DIRECTIONS.INBOUND : DIRECTIONS.OUTBOUND;

    const oppositeNonCanceledPassengers = [
      ...new Set(
        booking[oppositeDirection]
          .filter(({ cancelled }) => !cancelled)
          .map(({ products }) => products.filter(({ cancelled }) => !cancelled).map(({ passengerId }) => passengerId))
          .flat()
      )
    ];

    const hasSingleFare = selectedPassengers.some((id) => !oppositeNonCanceledPassengers.includes(id));
    const hasReturnFare = selectedPassengers.some((id) => oppositeNonCanceledPassengers.includes(id));
    const hasDifferentFares = (hasSingleFare && !hasReturnFare) || (!hasSingleFare && hasReturnFare);

    return isReturnJourney && selectedPassengers.length > 1 && !hasDifferentFares;
  } else {
    return false;
  }
};

const hasOppositeThroughFareWithMandatoryReturn = ({ segments, selectedItemRefs, booking }) => {
  if (selectedItemRefs.length === 0) return false;

  const selectedPassengersWithDirection = segments.reduce((acc, { direction, products }) => {
    const passengerIds = products.reduce((acc, { itemRef, passengerId }) => {
      if (selectedItemRefs.includes(itemRef)) {
        acc.push(passengerId);
      }

      return acc;
    }, []);

    if (!acc[direction]) {
      acc[direction] = new Set(passengerIds);
    } else {
      acc[direction].add(...passengerIds);
    }

    return acc;
  }, {});

  if (Object.keys(selectedPassengersWithDirection).length > 1) return false;

  const oppositeDirection =
    Object.keys(selectedPassengersWithDirection)[0] === DIRECTIONS.OUTBOUND ? DIRECTIONS.INBOUND : DIRECTIONS.OUTBOUND;

  const selectedPassengerIds = Object.values(selectedPassengersWithDirection)[0];

  return (
    booking[oppositeDirection]?.length > 0 &&
    booking[oppositeDirection].some((segment) => {
      if (segment.isThroughFare) {
        return segment.products.some(
          ({ passengerId, tariffDetails }) => selectedPassengerIds.has(passengerId) && tariffDetails?.mandatoryReturn === true
        );
      }

      return false;
    })
  );
};

const ticketsWithAopInOneJourney = ({ segments, booking: { additionalDetails = [] } = { additionalDetails: [] } }) => {
  const isMultiJourneyJourney = isBookingMultiJourney(additionalDetails);

  if (isMultiJourneyJourney) {
    const journeys = new Set();
    let hasAOP = false;

    segments.forEach(({ id, products }) => {
      const journey = additionalDetails.find(({ key }) => key === id)?.value;

      if (
        !hasAOP &&
        products?.some(
          ({ afterSalesCurrentOverrides }) =>
            afterSalesCurrentOverrides?.length > 0 && afterSalesCurrentOverrides.some(({ type }) => type === 'REBOOK')
        )
      ) {
        hasAOP = true;
      }

      if (journey) {
        journeys.add(journey);
      }
    });

    return hasAOP && journeys.size > 1 ? false : true;
  }

  return true;
};

const hasActiveAOP = ({ segments, selectedItemRefs }) => {
  const hasActiveAOP = segments.some(({ activeBookingItems }) =>
    activeBookingItems
      .filter(({ itemRef }) => selectedItemRefs.includes(itemRef))
      .some(
        ({ canBeRebooked, afterSalesCurrentOverrides }) =>
          afterSalesCurrentOverrides &&
          afterSalesCurrentOverrides.length > 0 &&
          afterSalesCurrentOverrides.some(({ type }) => type === 'REBOOK') &&
          canBeRebooked
      )
  );

  return hasActiveAOP;
};

const allSegmentsHaveSameSelectedPassengers = ({ segments, selectedItemRefs }) => {
  if (segments.length === 1) {
    return true;
  }

  const passengersBySegments = segments.map((segment) =>
    segment.products.filter((product) => selectedItemRefs.includes(product.itemRef)).map((product) => product.passenger.id)
  );

  const allPassengersBySegmentsHaveSameLength = passengersBySegments.every(
    (passengers) => passengers.length === passengersBySegments[0]?.length
  );

  if (!allPassengersBySegmentsHaveSameLength) {
    return false;
  }

  const passengerIds = new Set();
  passengersBySegments.forEach((passengers) => {
    passengers.forEach((passengerId) => {
      passengerIds.add(passengerId);
    });
  });

  return passengerIds.size === passengersBySegments[0]?.length;
};

const allSegmentsOfType =
  (types) =>
  ({ segments }) =>
    segments.every((segment) => types.includes(path(['classOfService', 'code'], segment)));

const allSegmentsOfService =
  (types) =>
  ({ segments }) =>
    segments.every((segment) => types.includes(path(['serviceType', 'code'], segment)));

const allSegmentsOfProductFamily =
  (productfamilies) =>
  ({ segments }) =>
    segments?.every(({ products }) => products?.every(({ productFamily }) => productfamilies.includes(productFamily?.familyGroup)));

const userCanOverrideAfterSalesRules = ({ user }) => user.allowAfterSalesOverrideRules;

const getRefunds = (payments = []) => payments.filter((payment) => payment.isSuccessful && payment.isBalance);
const getVoucherRefunds = (payments) => payments.find(isVoucherPayment);

const covidVoucherRefunded = (notes) => {
  const hasCovidVoucherRefund = notes.filter((note) => note.content.includes(COVID_VOUCHER_REFUND));

  return !!hasCovidVoucherRefund.length;
};

const hasSegmentDepartedMoreThan60DaysAgo = (segments) => {
  const hasSegment = segments?.some((segment) => moment(segment.timing.isoDate).isBefore(moment().subtract(60, 'days')));

  return Boolean(hasSegment);
};

const getNonCancelledItemRefs = ({ booking: { allBookingItems = [] } }) =>
  allBookingItems?.filter((bookingItem) => !bookingItem.cancelled)?.map((item) => item.itemRef);

const allSegmentsHaveSameCurrency = ({ segments, selectedItemRefs }) => {
  if (segments.length === 1) {
    return true;
  }

  const currenciesBySegments = segments
    .map((segment) => segment.products.filter((product) => selectedItemRefs.includes(product.itemRef)).map((product) => product.currency))
    .flat();

  return currenciesBySegments.every((c) => c === currenciesBySegments[0]);
};

const someSegmentsOfService =
  (types) =>
  ({ segments }) =>
    segments.some((segment) => types.includes(path(['serviceType', 'code'], segment)));

const allSegmentsInOneDirection = ({ segments }) => {
  const directions = new Set();

  segments.forEach(({ direction }) => directions.add(direction));
  return directions.size > 1 ? false : true;
};

const allSegmentsHaveOnePassengers = ({ segments }) =>
  segments.every(
    (segment) => segment.products.filter((product) => !product.isCancelled).map(({ passengerId }) => passengerId).length === 1
  );
const selectedPassengersOfType =
  (types) =>
  ({ segments, selectedItemRefs }) => {
    const products = segments.map(({ products }) => products).flat();
    const selectedPassengerTypes = [
      ...new Set(
        products.reduce((acc, { passenger: { type }, itemRef }) => {
          if (selectedItemRefs.includes(itemRef)) {
            return [...acc, type];
          }
          return acc;
        }, [])
      )
    ];
    return selectedPassengerTypes.every((type) => types.includes(type));
  };

const directionsOfSelectedSegments = ({ selectedItemRefs = [], booking = {} }) => [
  ...new Set(selectedItemRefs.map((itemRef) => findDirectionOfProductByItemRef(booking, itemRef)))
];

const selectedSegmentsContainsChildren = ({ selectedItemRefs, booking }) => {
  const selectedSegmentsDirections = directionsOfSelectedSegments({ selectedItemRefs, booking });

  return selectedSegmentsDirections
    ?.map((item) =>
      booking[item]?.map(({ products }) =>
        products.filter(({ cancelled }) => !cancelled)?.some(({ passenger }) => passenger?.type === types.CHILD)
      )
    )
    ?.flat()
    ?.some((item) => item === true);
};

const retrievePassengersFromItemRefs = ({ selectedItemRefs, itemRef, useSelectedItemRefs = true }) =>
  useSelectedItemRefs ? selectedItemRefs.includes(itemRef) : !selectedItemRefs.includes(itemRef);

const allPassengersAreChildren = ({ selectedItemRefs, booking, useSelectedItemRefs = true }) => {
  const selectedSegmentsDirections = directionsOfSelectedSegments({ selectedItemRefs, booking });

  const passengers = selectedSegmentsDirections
    ?.map((item) =>
      booking[item]
        ?.map(({ products }) =>
          products
            .filter(
              ({ cancelled, itemRef }) => !cancelled && retrievePassengersFromItemRefs({ selectedItemRefs, itemRef, useSelectedItemRefs })
            )
            ?.map(({ passenger }) => passenger.type)
        )
        ?.filter((p) => p.length > 0)
    )
    ?.flat();

  return passengers?.map((p) => p.length > 0 && p.every((passenger) => passenger === types.CHILD))?.some((item) => item === true);
};

const cancellationWillResultInChildAlone = ({ shouldCheckForChildrenAlone, selectedItemRefs, booking }) => {
  if (!shouldCheckForChildrenAlone) return false;
  return allPassengersAreChildren({ selectedItemRefs, booking, useSelectedItemRefs: false });
};

const exchangeWillResultInChildAlone = ({ shouldCheckForChildrenAlone, selectedItemRefs, booking }) => {
  if (!shouldCheckForChildrenAlone) return false;
  return (
    allPassengersAreChildren({ selectedItemRefs, booking, useSelectedItemRefs: false }) ||
    allPassengersAreChildren({ selectedItemRefs, booking })
  );
};
const hasSnapBooking = ({ segments, selectedItemRefs }) =>
  segments.some((segment) =>
    segment.products.some(
      (product) => selectedItemRefs.includes(product.itemRef) && product.productFamily.familyGroup === productFamilyCodes.LAST_MINUTE
    )
  );

const selectedProductsFromItemRefs = ({ segments = [], selectedItemRefs = [] }) =>
  segments.reduce(
    (acc, { products = [] }) => [...acc, ...(products?.filter(({ itemRef }) => selectedItemRefs.includes(itemRef)) || [])],
    []
  );

const segmentIsInMissedDeparturePeriod = ({ segments, selectedItemRefs }) => {
  const [segment] = segments;
  const productsSelected = selectedProductsFromItemRefs({ segments, selectedItemRefs });
  const thirtyMinsBeforeDeparture = moment(segment.timing.isoDate).subtract(30, 'minutes');
  const sixtyMinsBeforeDeparture = moment(segment.timing.isoDate).subtract(60, 'minutes');
  const sixtyMinsAfterDeparture = moment(segment.timing.isoDate).add(60, 'minutes');

  if (productsSelected?.some(({ productFamily }) => productFamily.familyGroup !== productFamilyCodes.PUB)) {
    return moment().isBetween(sixtyMinsBeforeDeparture, sixtyMinsAfterDeparture);
  }

  return moment(productsSelected?.[0]?.confirmedTimestamp).isBefore(moment(NEW_FARE_CONDITION_DATE_ON_EXCHANGE))
    ? moment().isBetween(thirtyMinsBeforeDeparture, sixtyMinsAfterDeparture)
    : moment().isBetween(sixtyMinsBeforeDeparture, sixtyMinsAfterDeparture);
};

const areAllSelectedProductsLoyal = ({ selectedItemRefs, booking }) => {
  const { allBookingItems } = booking;
  const selectedItemsProductFamilies = selectedItemRefs.flatMap((ref) =>
    allBookingItems.filter((item) => ref === item.itemRef).map((item) => item.productFamily.familyGroup)
  );
  if (selectedItemRefs.length > 0) {
    return selectedItemsProductFamilies.every((item) => item === productFamilyCodes.EUROSTAR_LOYALTY);
  } else return false;
};

const checkItemsAttribute = ({ itemRefs, journey, attribute }) => {
  const itemsWithAttribute = journey
    .flatMap(({ bookingItems }) => bookingItems.filter((item) => item[attribute]))
    .map(({ itemRef }) => itemRef);

  return itemRefs.some((ref) => itemsWithAttribute.includes(ref));
};

const containsSNItemRefs = (itemRefs, journey, every = true) => {
  const snItems = journey
    .filter(({ serviceType }) => SN_BRAND_CODE.includes(serviceType.brandCode))
    .flatMap(({ bookingItems }) => bookingItems.map(({ itemRef }) => itemRef));

  return every ? itemRefs.every((ref) => snItems.includes(ref)) : itemRefs.some((ref) => snItems.includes(ref));
};

const containsSNCBItemRefs = (itemRefs, journey, every = true) => {
  const sncbItems = journey
    .filter(({ serviceType }) => [...SNCB_BRAND_CODE].includes(serviceType?.brandCode))
    .flatMap(({ bookingItems }) => bookingItems?.map(({ itemRef }) => itemRef));

  return every ? itemRefs.every((ref) => sncbItems.includes(ref)) : itemRefs.some((ref) => sncbItems.includes(ref));
};

const allSegmentsSegmentsDepartInFuture = ({ segments, selectedItemRefs }) =>
  !segments?.length
    ? false
    : segments
        .filter(({ products }) => products.some(({ itemRef }) => selectedItemRefs.includes(itemRef)))
        .every((segment) => moment(segment.timing.isoDate).isSameOrAfter(moment()));

const checkPaxsSelectionToGeneratePDF = ({ booking, selectedItemRefs, uniquePassengers }) => {
  const oneDirectionSelected = runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsInOneDirection]);

  if (!oneDirectionSelected) {
    return uniquePassengers.length !== 1;
  }

  const oneSegment = runAfterSaleValidator(booking, selectedItemRefs)([oneSegmentSelected]);

  if (!oneSegment) {
    return !runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsHaveSameSelectedPassengers]);
  }

  return false;
};

const getBookingMagnetBoundDates = ({
  isMinDate,
  isReturnJourney,
  directionForSingleJourney,
  subscriptionDate,
  outbound = [],
  inbound = [],
  isExchangingSingleOutboundLegOfSNCBSummated,
  isExchangingSingleInboundLegOfSNCBSummated,
  hasSubscriptionProductOutbound,
  hasSubscriptionProductInbound,
  fixedExchangeDate
}) => {
  if (fixedExchangeDate) {
    const hasSubscriptionProduct =
      isReturnJourney || directionForSingleJourney === 'outbound'
        ? hasSubscriptionProductOutbound || hasSubscriptionProductInbound
        : hasSubscriptionProductInbound;
    const bound = subscriptionDate && hasSubscriptionProduct ? moment(subscriptionDate) : null;
    const fixedDate = moment(fixedExchangeDate);

    return bound ? (isMinDate ? moment.max(fixedDate, bound) : moment.min(fixedDate, bound)) : fixedDate;
  }

  // getMinDate
  if (isMinDate) {
    // Single calendar for outbound
    if (isReturnJourney || directionForSingleJourney === 'outbound') {
      // subscriptionStartDate
      if (subscriptionDate) {
        if (hasSubscriptionProductOutbound || hasSubscriptionProductInbound) return moment.max(moment(), moment(subscriptionDate));
        return isExchangingSingleOutboundLegOfSNCBSummated ? moment(outbound?.[0]?.timing?.date) : moment();
      }

      return isExchangingSingleOutboundLegOfSNCBSummated ? moment(outbound?.[0]?.timing?.date) : moment();
    }

    // Single calendar for inbound
    if (subscriptionDate) {
      if (hasSubscriptionProductInbound) return moment.max(moment(), moment(subscriptionDate));
      return isExchangingSingleInboundLegOfSNCBSummated ? moment(inbound?.[0]?.timing?.date) : moment();
    }

    return isExchangingSingleInboundLegOfSNCBSummated ? moment(inbound?.[0]?.timing?.date) : moment();
  }

  // getMaxDate
  // Single calendar for outbound
  if (isReturnJourney || directionForSingleJourney === 'outbound') {
    // subscriptionEndDate
    if (subscriptionDate) {
      if (hasSubscriptionProductOutbound || hasSubscriptionProductInbound) return moment(subscriptionDate);
      return isExchangingSingleOutboundLegOfSNCBSummated ? moment(outbound?.[0]?.timing?.date) : null;
    }

    return isExchangingSingleOutboundLegOfSNCBSummated ? moment(outbound?.[0]?.timing?.date) : null;
  }

  // Single calendar for inbound
  if (subscriptionDate) {
    if (hasSubscriptionProductInbound) return moment(subscriptionDate);
    return isExchangingSingleInboundLegOfSNCBSummated ? moment(inbound?.[0]?.timing?.date) : null;
  }

  return isExchangingSingleInboundLegOfSNCBSummated ? moment(inbound?.[0]?.timing?.date) : null;
};

const selectedItemsHaveLuggageExtras = (booking, selectedItemRefs) => {
  const { outbound, inbound } = booking;

  const filteredOutbound = filterLegsByProductItemRefs(outbound, selectedItemRefs);
  const filteredOutboundPaxs = filteredOutbound
    .map((fo) => fo.bookingItems?.filter((bi) => selectedItemRefs.includes(bi.itemRef)).map((bi) => bi.passengerId))
    .flat();

  const filteredInbound = filterLegsByProductItemRefs(inbound, selectedItemRefs);
  const filteredInboundPaxs = filteredInbound
    .map((fo) => fo.bookingItems?.filter((bi) => selectedItemRefs.includes(bi.itemRef)).map((bi) => bi.passengerId))
    .flat();

  const outboundExtras = filteredOutbound
    .map((fo) => fo.extraProducts || [])
    .flat()
    .filter((ep) => !ep.cancelled && ep.type === 'LU' && filteredOutboundPaxs.includes(ep.passenger.id));

  const inboundExtras = filteredInbound
    .map((fo) => fo.extraProducts || [])
    .flat()
    .filter((ep) => !ep.cancelled && ep.type === 'LU' && filteredInboundPaxs.includes(ep.passenger.id));

  const isInboundESOnlyAndHasExtras =
    filteredInbound.every((leg) => leg?.serviceType?.code === serviceTypeCodes.EUROSTAR) && inboundExtras.length > 0;

  const isOutboundESOnlyAndHasExtras =
    filteredOutbound.every((leg) => leg?.serviceType?.code === serviceTypeCodes.EUROSTAR) && outboundExtras.length > 0;

  return isInboundESOnlyAndHasExtras || isOutboundESOnlyAndHasExtras;
};

const hasOppositeWithMandatoryReturnOnTemporaryOnHold = ({ segments, booking, selectedItemRefs }) => {
  if (selectedItemRefs.length === 0 || booking.status !== bookingStatusTypes.TEMPORARILY_ON_HOLD) return false;

  const isEurostarBlue = [...booking.outbound, ...booking.inbound]?.every((leg) => leg.serviceType?.code === serviceTypeCodes.EUROSTAR);
  if (!isEurostarBlue) return false;

  const selectedPassengersWithDirection = segments.reduce((acc, { direction, products }) => {
    const passengerIds = products.reduce((acc, { itemRef, passengerId }) => {
      if (selectedItemRefs.includes(itemRef)) {
        acc.push(passengerId);
      }

      return acc;
    }, []);

    if (!acc[direction]) {
      acc[direction] = new Set(passengerIds);
    } else {
      acc[direction].add(...passengerIds);
    }

    return acc;
  }, {});

  if (Object.keys(selectedPassengersWithDirection).length > 1) return false;

  const oppositeDirection =
    Object.keys(selectedPassengersWithDirection)[0] === DIRECTIONS.OUTBOUND ? DIRECTIONS.INBOUND : DIRECTIONS.OUTBOUND;

  const selectedPassengerIds = Object.values(selectedPassengersWithDirection)[0];

  return (
    booking[oppositeDirection]?.length > 0 &&
    booking[oppositeDirection].some((segment) => segment.products.some(({ passengerId }) => selectedPassengerIds.has(passengerId)))
  );
};

const getFaresForPassengersInMixedJourney = ({ booking = {}, selectedItemRefs = [], hasPassengersWithMixedJourneys = false }) => {
  const selectedSegmentsDirections = directionsOfSelectedSegments({ selectedItemRefs, booking });
  // Check already made for the message, so skip if false or for a return journey
  // selection as uneven passengers aftersales are not supported
  if (!hasPassengersWithMixedJourneys || selectedSegmentsDirections.length > 1)
    return {
      singleFares: [],
      returnFares: []
    };

  const getPassengersAndTickets = selectedSegmentsDirections
    ?.map((item) =>
      booking[item]
        .filter(({ cancelled }) => !cancelled)
        ?.flatMap(({ products }) => products?.filter(({ cancelled, itemRef }) => !cancelled && selectedItemRefs.includes(itemRef)))
        ?.flatMap(({ ticketNumber, passengerId, passenger }) => ({
          ticketNumber,
          passengerId,
          passenger
        }))
    )
    ?.flat();

  const getOppositeDirection = selectedSegmentsDirections?.[0] === DIRECTIONS.OUTBOUND ? DIRECTIONS.INBOUND : DIRECTIONS.OUTBOUND;
  const getOppositePassengers = booking[getOppositeDirection]
    ?.filter(({ cancelled }) => !cancelled)
    ?.flatMap(({ products }) =>
      products?.filter(({ passengerId }) => getPassengersAndTickets.some((item) => item.passengerId === passengerId))
    )
    ?.flatMap(({ passengerId, cancelled, passenger }) => ({
      passengerId,
      cancelled,
      passenger
    }));

  return {
    singleFares: getPassengersAndTickets.filter(
      ({ passengerId }) =>
        getOppositePassengers.some((item) => item.passengerId === passengerId && item.cancelled) ||
        !getOppositePassengers.find((s) => s.passengerId === passengerId)
    ),
    returnFares: getPassengersAndTickets.filter(({ passengerId }) =>
      getOppositePassengers.some((item) => item.passengerId === passengerId && !item.cancelled)
    )
  };
};

const getProductFamiliesPerPassengersInMixedPF = ({ booking = {}, selectedItemRefs = [], hasMoreThanOneProductFamily = false }) => {
  if (!hasMoreThanOneProductFamily)
    return {
      outbound: [],
      inbound: []
    };

  const { outbound, inbound } = booking;

  const getPassengersAndTicketsOutbound =
    outbound
      ?.filter(({ cancelled }) => !cancelled)
      ?.flatMap(({ products }) => products?.filter(({ cancelled, itemRef }) => !cancelled && selectedItemRefs.includes(itemRef)))
      ?.flatMap(({ ticketNumber, passengerId, productFamily, passenger }) => ({
        ticketNumber,
        passengerId,
        passenger,
        productFamily: productFamily?.familyGroupName || productFamily?.familyGroup
      }))
      ?.flat() ?? [];

  const getPassengersAndTicketsInbound =
    inbound
      ?.filter(({ cancelled }) => !cancelled)
      ?.flatMap(({ products }) => products?.filter(({ cancelled, itemRef }) => !cancelled && selectedItemRefs.includes(itemRef)))
      ?.flatMap(({ ticketNumber, passengerId, productFamily, passenger }) => ({
        ticketNumber,
        passengerId,
        passenger,
        productFamily: productFamily?.familyGroupName || productFamily?.familyGroup
      }))
      ?.flat() ?? [];

  if (
    [...getPassengersAndTicketsOutbound, ...getPassengersAndTicketsInbound]?.every(
      ({ productFamily }) => productFamily === [...getPassengersAndTicketsOutbound, ...getPassengersAndTicketsInbound]?.[0]?.productFamily
    )
  )
    return {
      outbound: [],
      inbound: []
    };
  return {
    outbound: getPassengersAndTicketsOutbound,
    inbound: getPassengersAndTicketsInbound
  };
};

const getProductFamiliesOfSelectedTickets = ({ booking = {}, selectedItemRefs = [] }) => {
  const { outbound = [], inbound = [] } = booking;

  return (
    [...outbound, ...inbound]
      ?.filter(({ cancelled }) => !cancelled)
      ?.flatMap(({ products }) => products?.filter(({ cancelled, itemRef }) => !cancelled && selectedItemRefs.includes(itemRef)))
      ?.flatMap(({ productFamily }) => productFamily?.familyGroup)
      ?.flat() ?? []
  );
};

const allSelectedItemsAreOnCarriers = ({
  itemRefs = [],
  segments = [],
  brandCodes = [...SN_BRAND_CODE, ...SNCB_BRAND_CODE, ...DB_BRAND_CODES, ...NS_BRAND_CODE]
}) => {
  // Only for DB for now
  const canBeLeftOnTheirOwn = segments.some(isDBLeg);
  if (canBeLeftOnTheirOwn) return itemRefs.length > 0;

  const selectedItemRefsBasedOnCarriers = segments
    .filter(({ serviceType }) => brandCodes.includes(serviceType?.brandCode))
    .flatMap(({ bookingItems }) => bookingItems?.map(({ itemRef }) => itemRef));

  return itemRefs.length > 0 && itemRefs.every((ref) => selectedItemRefsBasedOnCarriers.includes(ref));
};

const hasDBTicketsSelected = ({ segments = [] }) => segments.some(({ serviceType }) => serviceType?.code === serviceTypeCodes.DB);

const selectedProductsHaveAssociatedNonSelectedExternalCarrierProducts = ({
  booking: { outbound = [], inbound = [] } = {},
  selectedItemRefs = []
}) => {
  const associatedItemRefsFromSelectedItems = selectedItemRefs.flatMap((selectedItemRef) =>
    [...outbound, ...inbound].flatMap(({ products = [] }) =>
      products.filter((product) => selectedItemRef === product.itemRef)?.flatMap((product) => product.associatedItemRefs)
    )
  );

  const unselectedAssociatedItemRefsBrandCodes = [
    ...new Set(
      [...outbound, ...inbound].flatMap(({ serviceType, products = [] }) =>
        products
          .filter((product) => associatedItemRefsFromSelectedItems.includes(product.itemRef) && !selectedItemRefs.includes(product.itemRef))
          .map(() => serviceType?.brandCode)
      )
    )
  ];

  return unselectedAssociatedItemRefsBrandCodes.some((code) => [...NS_BRAND_CODE, ...SNCB_BRAND_CODE, ...SN_BRAND_CODE].includes(code));
};

const areSomeExternalCarrierProductsSelected = ({
  journeys = [],
  selectedItemRefs = [],
  externalCarriers = [...SN_BRAND_CODE, ...SNCB_BRAND_CODE, ...NS_BRAND_CODE, ...DB_BRAND_CODES]
}) =>
  journeys.some(({ serviceType = {}, products = [] }) =>
    products.some((product) => selectedItemRefs.includes(product.itemRef) && externalCarriers.includes(serviceType.brandCode))
  );

const areLoyaltyCompanionTicketsSelected = ({ segments = [], selectedItemRefs = [] }) =>
  segments.some((segment) =>
    segment.products?.some(
      (product) =>
        selectedItemRefs.includes(product.itemRef) && product.productFamily?.familyGroup === productFamilyCodes.LOYALTY_COMPANIOIN
    )
  );

export {
  checkItemsAttribute,
  containsSNItemRefs,
  runAfterSaleValidator,
  bookingIsConfirmed,
  selectedProductsCheckedIn,
  allSelectedProductsConfirmed,
  productHasNotBeenPrrRefunded,
  oneSegmentSelected,
  allSegmentsDepartToday,
  allSegmentsAreMinutesBeforeDeparture,
  allSegmentsHaveSameSelectedPassengers,
  allSegmentsOfService,
  allSegmentsOfType,
  getAssociatedItemRefsForSelectedItems,
  allSegmentsCanBeRebooked,
  allSegmentsCannotBeRebooked,
  allSegmentsCanBeCancelled,
  userCanOverrideAfterSalesRules,
  noItemsSelectedOrStatusNotAllowed,
  getTicketNumberForCheckedInTickets,
  getTicketNumbers,
  getSegmentsForItemRefs,
  getRefunds,
  getVoucherRefunds,
  covidVoucherRefunded,
  hasSegmentDepartedMoreThan60DaysAgo,
  getNonCancelledItemRefs,
  allSegmentsHaveSameCurrency,
  allSegmentsInOneJourney,
  canceledPassengersInSegments,
  ticketsWithAopInOneJourney,
  findDirectionOfProductByItemRef,
  selectedItemsArePartOfASummatedFare,
  someSegmentsOfService,
  passengersWithMixedJourneysInSegments,
  allProductsHasNotBeenPrrRefunded,
  allSegmentsInOneDirection,
  selectedProductsThroughFare,
  selectedProductsFamilyAllowedForAddPax,
  selectedProductsCheckedInOrConfirmedOrPrrAttempted,
  disableAfterSalesForSubscription,
  allSegmentsHaveOnePassengers,
  selectedPassengersOfType,
  selectedProductsHaveDifferentProductFamily,
  hasActiveAOP,
  getPassengersForItemRefs,
  getUniquePassengersbyId,
  allSelectedProductsConfirmedCheckedInOrPRRRefunded,
  directionsOfSelectedSegments,
  allPassengersAreChildren,
  selectedSegmentsContainsChildren,
  exchangeWillResultInChildAlone,
  cancellationWillResultInChildAlone,
  hasOppositeThroughFareWithMandatoryReturn,
  selectedProductsFromItemRefs,
  segmentIsInMissedDeparturePeriod,
  hasActiveInsurance,
  areAllSelectedProductsLoyal,
  isBeforeToday,
  isDepartureBeforeToday,
  hasSnapBooking,
  allSegmentsOfProductFamily,
  selectedProductsCheckedInOrPrrAttempted,
  allSegmentsSegmentsDepartInFuture,
  checkPaxsSelectionToGeneratePDF,
  checkIsEurostarRedAndBlue,
  getBookingMagnetBoundDates,
  containsSNCBItemRefs,
  selectedItemsHaveLuggageExtras,
  hasOppositeWithMandatoryReturnOnTemporaryOnHold,
  getFaresForPassengersInMixedJourney,
  getProductFamiliesPerPassengersInMixedPF,
  getProductFamiliesOfSelectedTickets,
  allSelectedItemsAreOnCarriers,
  hasDBTicketsSelected,
  selectedProductsHaveAssociatedNonSelectedExternalCarrierProducts,
  areSomeExternalCarrierProductsSelected,
  areLoyaltyCompanionTicketsSelected
};
